import { useEffect, useLayoutEffect } from 'react';

import { isBrowserRestoringScroll } from 'helpers/ClientUtils';

// useLayoutEffect fires before paint but warns during SSR — fall back on server.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * PLP scroll restoration for the PDP → Back → PLP flow.
 *
 * PDP lives in a separate app (kratos), so Back is a full document load. The
 * browser restores the scroll offset, but the PLP then reflows in waves as late
 * content (ads/recos) loads in above the grid, drifting the
 * position. The server hides #root pre-paint on PLP loads (see serverConfig.js +
 * global.scss); this hook records the clicked card on the way out and, on a
 * Back/Forward load, re-anchors it through the reflow, then reveals #root once
 * settled. On any other load it just reveals immediately. Reveal = add `plpReady`.
 */

const STORAGE_KEY = 'plp:clickedProduct';
const READY_CLASS = 'plpReady';
// Re-anchor for at least MIN (reflow arrives in waves with lulls) and at most MAX
// (grid may stream in after a loader), settling when doc height holds STABLE frames.
const MAX_RESTORE_MS = 8000;
const MIN_RESTORE_MS = 2000;
const STABLE_FRAMES = 20;

const getPlpUrl = () => `${window.location.pathname}${window.location.search}`;
const reveal = () => document.documentElement.classList.add(READY_CLASS);
const docHeight = () => Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, document.body.offsetHeight);

export const usePlpScrollRestoration = () => {
  useIsomorphicLayoutEffect(() => {
    // Record the clicked product on the way out (capture phase, before the Link).
    const onClick = (event: MouseEvent) => {
      const styleId = (event.target as Element | null)?.closest<HTMLElement>('[data-style-id]')?.dataset.styleId;
      if (!styleId) return;
      try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ url: getPlpUrl(), styleId }));
      } catch {
        /* sessionStorage unavailable — skip */
      }
    };
    document.addEventListener('click', onClick, { capture: true });

    let saved: { url: string; styleId: string } | null = null;
    try {
      saved = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch {
      saved = null;
    }

    // Only re-anchor on a Back/Forward load with a saved product for this URL.
    // Anything else: reveal now so the pre-paint mask never affects a normal visit.
    if (!(isBrowserRestoringScroll() && saved?.url === getPlpUrl())) {
      reveal();
      return () => document.removeEventListener('click', onClick, { capture: true });
    }

    const { styleId } = saved;
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }

    let rafId = 0;
    let stopped = false;
    let stableCount = 0;
    let lastHeight = NaN;
    const deadline = performance.now() + MAX_RESTORE_MS;
    const floor = performance.now() + MIN_RESTORE_MS;

    // Reveal #root only here — once the card is settled, we've bailed, or timed
    // out — so the whole restore/reflow transition stays hidden behind the mask.
    const stop = () => {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(rafId);
      ['wheel', 'touchstart', 'keydown'].forEach(t => window.removeEventListener(t, stop));
      reveal();
    };

    // One check per frame: keep the clicked card centered while the page reflows.
    // The waves grow doc height, so "height held steady for STABLE_FRAMES after the
    // floor" is the settled signal — the card's post-scroll position can't be used
    // (scrollIntoView keeps it centered every frame).
    const tick = () => {
      if (stopped) return;
      const card = document.querySelector<HTMLElement>(`#products [data-style-id="${styleId}"]`);
      if (card) card.scrollIntoView({ block: 'center' });

      const height = docHeight();
      stableCount = height === lastHeight ? stableCount + 1 : 0;
      lastHeight = height;

      const now = performance.now();
      if (card && now >= floor && stableCount >= STABLE_FRAMES) return stop();
      if (now >= deadline) return stop();
      rafId = requestAnimationFrame(tick);
    };

    // The user taking over always wins.
    ['wheel', 'touchstart', 'keydown'].forEach(t => window.addEventListener(t, stop, { passive: true }));
    rafId = requestAnimationFrame(tick);

    return () => {
      document.removeEventListener('click', onClick, { capture: true });
      stop();
    };
  }, []);
};

export default usePlpScrollRestoration;
